'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import { Formik, Form } from 'formik';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { toast } from 'sonner';

import { addressValidation } from '@/utils/validation/user';
import { addUpdateUserAddress } from '@/services/user/addresses';
import { getRegionsV2 } from '@/services/checkout';

import Input from '@/components/ui/inputFormik';
import InputMobile from '@/components/ui/inputFormik/input-mobile';
import SelectInputFormik from '@/components/ui/inputFormik/select-input-formik';
import { Button } from '@/components/ui/buttons';
import { Loading, Spinner } from '@/components/ui/forms';

const PHONE_PREFIX = '00966';
const POSTCODE = '10577';

function AddressForm({ selectedAddress, mage, onSuccess }) {
  const t = useTranslations();
  const locale = useLocale();
  const { data: session } = useSession();
  const isEditMode = Boolean(selectedAddress?.id);
  const [selectedArea, setSelectedArea] = useState({ label: '', value: '' });

  const { data: regionsData, isLoading: isRegionsLoading } = useSWR(
    `/mstore/region/${locale}`,
    () => getRegionsV2(locale),
    {
      revalidateOnFocus: false,
      onError: (error) => toast.error(error.message),
    }
  );

  const { trigger: submitAddress, isMutating } = useSWRMutation(
    ['/api/user/addresses', mage],
    ([, mage], { arg }) => addUpdateUserAddress(mage, arg),
    {
      onSuccess: (data) => {
        if (data?.data) {
          const message = isEditMode
            ? t('common.update_success_key')
            : t('user.address_create_success_key');
          toast.success(message);
          onSuccess?.();
        } else {
          toast.error(data?.error?.error?.message);
        }
      },
      onError: (error) => toast.error(error?.error?.error?.message),
    }
  );

  const formatPhoneNumber = (phone) => (phone ? `0${phone.slice(-9)}` : '');

  const getRegionById = (regionId) =>
    regionsData?.data?.data
      ?.slice(1)
      .find((region) => region.value === regionId);

  const getInitialValues = () => {
    if (isEditMode) {
      return {
        firstname: selectedAddress.firstname,
        lastname: selectedAddress.lastname,
        phone: formatPhoneNumber(selectedAddress.telephone),
        city: selectedAddress?.custom_attributes?.[0]?.value,
        shortAddress: selectedAddress.street[0],
        address: selectedAddress.street[1] || selectedAddress.street[0],
        region_id: String(selectedAddress.region_id),
      };
    }
    return {
      firstname: session?.user?.firstname || '',
      lastname: session?.user?.lastname || '',
      phone: formatPhoneNumber(session?.user?.mobilenumber),
      city: '',
      shortAddress: '',
      address: '',
      region_id: '',
    };
  };

  const buildPayload = (values) => {
    const cityData = regionsData?.data?.data.find(
      (r) => r.value == values?.region_id
    );
    const currentArea = cityData?.areas?.find(
      (item) => item?.city_id == values?.city
    );

    return {
      id: isEditMode ? selectedAddress.id : undefined,
      customer_id: session?.user?.id,
      region: {
        region_code: '',
        region: cityData?.label,
        region_id: parseInt(cityData?.value),
      },
      region_id: parseInt(cityData?.value),
      country_id: cityData?.country_id,
      street: [values.shortAddress, values.address],
      telephone: `${PHONE_PREFIX}${values.phone}`,
      postcode: POSTCODE,
      city: currentArea?.name,
      custom_attributes: {
        city_id: parseInt(currentArea?.city_id),
      },
      firstname: values.firstname,
      lastname: values.lastname,
      default_billing: selectedAddress?.default_billing || false,
      default_shipping: selectedAddress?.default_shipping || false,
    };
  };

  const handleSubmit = async (values) => {
    const payload = buildPayload(values);
    await submitAddress(payload);
  };

  const handleCityChange = (selectedCity, setFieldValue) => {
    const regionData = getRegionById(selectedCity.value);
    const firstArea = regionData?.areas?.[0];

    if (firstArea) {
      const areaOption = { label: firstArea.name, value: firstArea.city_id };
      setSelectedArea(areaOption);
      setFieldValue('city', firstArea.city_id);
    }

    setFieldValue('region_id', selectedCity.value);
    setFieldValue('country_id', regionData?.country_id || '');
  };

  const handleAreaChange = (
    selectedAreaOption,
    setFieldValue,
    selectedCityData
  ) => {
    const areaData = selectedCityData?.areas?.find(
      (area) => area?.city_id == selectedAreaOption.value
    );

    if (areaData) {
      setSelectedArea(selectedAreaOption);
      setFieldValue('city', areaData.city_id);
      setFieldValue('region', areaData.name);
      setFieldValue(
        'country_id',
        areaData.country_id || selectedCityData?.country_id
      );
    }
  };

  if (isRegionsLoading) {
    return (
      <div className='flex h-40 items-center justify-center'>
        <Loading />
      </div>
    );
  }

  const regions = regionsData?.data?.data?.slice(1) || [];
  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={addressValidation(t)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, touched, setFieldValue, values }) => {
        const selectedCityData = getRegionById(values.region_id);
        const availableAreas =
          selectedCityData?.areas?.map((area) => ({
            label: area.name,
            value: area.city_id,
          })) || [];

        return (
          <Form className='grid gap-4 max-sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2'>
            <Input
              name='firstname'
              title={t('user.first_name_key')}
              mandatory
            />
            <Input name='lastname' title={t('user.last_name_key')} mandatory />
            <div className='lg:col-span-full'>
              <InputMobile
                name='phone'
                title={t('user.phone_number_key')}
                mandatory
              />
            </div>
            <SelectInputFormik
              id='city'
              name='city'
              title={t('user.select_city_key')}
              options={regions}
              defaultValue={
                isEditMode
                  ? regions.find((c) => c?.value == selectedAddress?.region_id)
                  : null
              }
              handleChange={(selected) =>
                handleCityChange(selected, setFieldValue)
              }
              errors={errors}
              touched={touched}
              mandatory
            />
            <SelectInputFormik
              id='region_id'
              name='region_id'
              title={t('user.select_region_key')}
              options={availableAreas}
              defaultValue={
                isEditMode
                  ? selectedArea?.value
                    ? selectedArea
                    : availableAreas.find(
                        (a) =>
                          a?.value ==
                          selectedAddress?.custom_attributes?.[0]?.value
                      ) || {
                        label:
                          regions.find(
                            (c) => c?.value == selectedAddress?.region_id
                          )?.areas?.[0]?.name || '',
                        value:
                          regions.find(
                            (c) => c?.value == selectedAddress?.region_id
                          )?.areas?.[0]?.city_id || '',
                      }
                  : selectedArea?.value
                    ? selectedArea
                    : null
              }
              handleChange={(selected) =>
                handleAreaChange(selected, setFieldValue, selectedCityData)
              }
              errors={errors}
              touched={touched}
              mandatory
            />{' '}
            <div className='lg:col-span-full'>
              <Input
                name='shortAddress'
                title={t('user.short_address_key')}
                mandatory
              />
            </div>
            <div className='col-span-full'>
              <Input
                name='address'
                title={t('user.address_key')}
                as='textarea'
                rows='7'
                mandatory
              />
            </div>
            <div className='col-span-full'>
              <Button type='submit' disabled={isMutating}>
                {isEditMode
                  ? t('user.edit_address_key')
                  : t('user.add_address_key')}
                {isMutating && (
                  <Spinner className='ml-2 h-4 w-4 animate-spin' />
                )}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AddressForm;
